/**
 * Sign In Route
 * Two-panel grid layout (columns on desktop, rows on portrait)
 * Grayscale colors only from launCh.css
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { genHash, isValidEmail } from '../utils/hash.js';
import AuthController from '../services/AuthController.js';
import { MicrocopyManager } from '../../../../shared/meta_microcopy_i18n.js';

export function render() {
  return `
    <div class="auth-container">
      <div class="auth-left">
        <h1 data-meta-copywrite-i118n='{"token": "welcome_back", "attribute": "textContent"}'>Welcome Back</h1>
        <p data-meta-copywrite-i118n='{"token": "please_enter_your_details", "attribute": "textContent"}'>Sign in to continue</p>

        <div class="auth-content">
          <form id="loginForm" class="auth-form" novalidate>
            <div class="form-group">
              <label for="email" data-meta-copywrite-i118n='{"token": "email", "attribute": "textContent"}'>EMAIL</label>
              <input
                type="text"
                id="email"
                name="email"
                placeholder="Enter your email"
                data-meta-copywrite-i118n='{"token": "enter_your_email", "attribute": "placeholder"}'
                autocomplete="email"
              />
            </div>

            <div class="form-group">
              <label for="password" data-meta-copywrite-i118n='{"token": "password", "attribute": "textContent"}'>PASSWORD</label>
              <div class="password-input-wrapper">
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  data-meta-copywrite-i118n='{"token": "enter_your_password", "attribute": "placeholder"}'
                  autocomplete="current-password"
                />
                <button type="button" id="togglePassword" class="toggle-password" aria-label="Toggle password visibility">
                  <svg id="eyeIcon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </button>
              </div>
            </div>

            <div class="form-footer">
              <a href="#/forgot" class="link-brand--uc__underline" data-meta-copywrite-i118n='{"token": "forgot_password", "attribute": "textContent"}'>FORGOT PASSWORD</a>
            </div>

            <button type="submit" class="btn" data-meta-copywrite-i118n='{"token": "sign_in", "attribute": "textContent"}'>SIGN IN</button>
          </form>

          <p class="auth-switch">
            <span data-meta-copywrite-i118n='{"token": "dont_have_an_account", "attribute": "textContent"}'>START YOUR FREE ACCOUNT</span> &nbsp;  <a class="link-brand--uc__underline" href="#/signup" data-meta-copywrite-i118n='{"token": "sign_up", "attribute": "textContent"}'>SIGN UP</a>
          </p>
        </div>
      </div>

      <div class="auth-right">
        <div class="auth-right-content">
          <div class="h-display__portrait--none">
            <br><br>
          </div>
          <h5 class="auth-hero-title">
            Stream Sync Engage is a delightful and easy way to send targeted instant messages to your app users. 
          </h5>
          <p class="auth-hero-description">
            You can easily send custom designed notifications, in-app surveys, product tours, voice mail, and slideshows. 
          </p>
        </div>
      </div>
    </div>
  `;
}

export function init() {
  console.log('[SigninRoute] Initialized');

  const form = document.getElementById('loginForm');
  const emailInput = document.getElementById('email');
  const passwordInput = document.getElementById('password');
  const togglePasswordBtn = document.getElementById('togglePassword');

  // Password visibility toggle
  togglePasswordBtn?.addEventListener('click', () => {
    const type = passwordInput.type === 'password' ? 'text' : 'password';
    passwordInput.type = type;
  });

  // Form submit handler
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    // Sequential validation - stop on first error
    if (!validateEmail(email, emailInput)) return;
    if (!validatePassword(password, passwordInput)) return;

    // Proceed with API call
    await handleSignin(email, password);
  });

  // Prevent default navigation for forgot password link
  document.getElementById('link--uc__underline')?.addEventListener('click', (e) => {
    e.preventDefault();
    PubSub.publish(AppBroadcast.AUTH_FORGOT);
  });

  // Prevent default navigation for sign up link - use ROUTE_NAV
  const signupLink = document.querySelector('.auth-switch a[href="#/signup"]');
  signupLink?.addEventListener('click', (e) => {
    e.preventDefault();
    console.log('[SigninRoute] Sign up link clicked - navigating to signup');
    PubSub.publish(AppBroadcast.ROUTE_NAV, { route: 'signup' });
  });
}

/**
 * Validate email field - shake and toast on error
 */
function validateEmail(email, inputElement) {
  // Check if empty
  if (!email) {
    shakeAndToast(inputElement, MicrocopyManager.get('validation_email_required'));
    return false;
  }

  // Check format (must contain exactly one @ and at least one .)
  const atCount = (email.match(/@/g) || []).length;
  const dotCount = (email.match(/\./g) || []).length;

  if (atCount !== 1 || dotCount < 1) {
    shakeAndToast(inputElement, MicrocopyManager.get('validation_email_invalid'));
    return false;
  }

  return true;
}

/**
 * Validate password field - shake and toast on error
 */
function validatePassword(password, inputElement) {
  // Check if empty
  if (!password) {
    shakeAndToast(inputElement, MicrocopyManager.get('validation_password_required'));
    return false;
  }

  // Check length (min 10 chars)
  if (password.length < 10) {
    shakeAndToast(inputElement, MicrocopyManager.get('validation_password_min_length'));
    return false;
  }

  return true;
}

/**
 * Shake element and show toast notification
 */
function shakeAndToast(element, message) {
  // Toast notification
  if (typeof neodigmToast !== 'undefined') {
    neodigmToast.q(message, 'danger');
  }

  // Shake animation
  if (typeof neodigmUtils !== 'undefined') {
    neodigmUtils.shake(`#${element.id}`);
  } else {
    // Fallback if neodigmUtils not available
    element.classList.add('shake');
    setTimeout(() => element.classList.remove('shake'), 300);
  }

  // Focus the invalid field
  element.focus();
}

/**
 * Handle signin API call
 */
async function handleSignin(email, password) {
  const submitBtn = document.querySelector('.btn');
  submitBtn.disabled = true;
  submitBtn.textContent = MicrocopyManager.get('loading_signing_in');

  try {
    // Hash password client-side
    const passwordHash = genHash(password);

    // Call AuthController.doSignin() - handles API call, token storage, and navigation
    const result = await AuthController.doSignin(email, passwordHash);

    if (result.ok) {
      // Success - AuthController handles token storage, entity persistence, and navigation
      console.log('[SigninRoute] Signin successful');

      // Toast notification
      if (typeof neodigmToast !== 'undefined') {
        neodigmToast.q(MicrocopyManager.get('success_welcome_back'), 'success');
      }

      // Publish success event
      PubSub.publish(AppBroadcast.AUTH_SIGNIN_SUCCESS, {
        entity: AuthController.getEntity()
      });
    } else if (result.resetRequired) {
      // Password reset required - AuthController handles navigation
      console.log('[SigninRoute] Password reset required');
      if (typeof neodigmToast !== 'undefined') {
        neodigmToast.q(MicrocopyManager.get('error_password_reset_required'), 'warning');
      }
    } else {
      // Error from API
      if (typeof neodigmToast !== 'undefined') {
        neodigmToast.q(result.error || MicrocopyManager.get('error_signin_failed'), 'danger');
      }
      submitBtn.disabled = false;
      submitBtn.textContent = MicrocopyManager.get('sign_in').toUpperCase();
    }
  } catch (error) {
    console.error('[SigninRoute] Sign in error:', error);
    if (typeof neodigmToast !== 'undefined') {
      neodigmToast.q(error.message || MicrocopyManager.get('error_network'), 'danger');
    }
    submitBtn.disabled = false;
    submitBtn.textContent = MicrocopyManager.get('sign_in').toUpperCase();

    PubSub.publish(AppBroadcast.AUTH_SIGNIN_FAIL, {
      message: error.message
    });
  }
}
